import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { DetailValue } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { postRemoveLock } from 'api/pbmAPI';
import { handleError } from 'services/Error';
import { useGetSupplierDetailsQuery } from 'store/api/referenceApi';

const Title = 'Remove Lock';

const ReasonOptions = [
    'Transition fill needed',
    'Client override to allow OON fill',
    'Member termed - lock did not term',
    'Group provision changed - lock did not term',
];

export const RemoveLockDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
    lockDetails = undefined,
    submitCallback,
}) => {
    const { treatmentId, payerId } = treatmentDetails ?? {};
    const { supplierNpi } = lockDetails ?? {};

    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        {
            supplierNpi,
        },
        {
            skip: !supplierNpi,
        },
    );

    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                ...ReasonInput.validationSchema,
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    // Call PBM service to unlock treatment here.
                    await postRemoveLock({
                        payerId,
                        treatmentId,
                        ...ReasonInput.submitValues(values),
                    });
                    submitCallback();
                    SuccessToast({ message: 'Lock removed from treatment successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to remove lock from treatment. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Confirm Remove Lock"
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="No, Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Yes, Remove Lock"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <div className="margin__bottom--medium">
                                Are you sure you want to remove the lock on this treatment for the
                                following pharmacy?
                            </div>
                            <div className="margin__bottom--medium">
                                <DetailValue value={supplierDetails?.organizationName} />
                            </div>
                            <ReasonInput reasons={ReasonOptions} />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

RemoveLockDialog.Title = Title;

RemoveLockDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        treatmentId: PropTypes.string.isRequired,
        payerId: PropTypes.string.isRequired,
    }),
    lockDetails: PropTypes.shape({
        supplierNpi: PropTypes.string,
    }),
    submitCallback: PropTypes.func.isRequired,
};
